import { Card } from "shared/components/Card";
import { Switch } from "shared/components/forms/Switch";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  ApartmentsComVariant,
  Unit,
  ZillowVariant
} from "types/landlord/address";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { invalidateNeedsLockboxCounts } from "shared/utils/units-cache";
import { useSettingsController } from "views/settings-integration/controller";
import { PropertyKind } from "shared/types";
import { Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { BoomBrandMark } from "shared/components/SyndicationChannels/BoomBrandMark";
import { SourceCheckbox } from "shared/components/SyndicationChannels/SourceCheckbox";
import { SourceLogo } from "shared/components/SyndicationChannels/SourceLogo";
import { TreeRow } from "shared/components/SyndicationChannels/TreeRow";
import {
  SyndicationState,
  apartmentsVariantForKind,
  diffMagicLinkPayload,
  readSyndicationState,
  withAffordableHousingComToggled,
  withApartmentsComToggled,
  withApartmentsVariant,
  withDwellsyToggled,
  withRentalBeastToggled,
  withUloopToggled,
  withRentalSourceToggled,
  withRentDuckToggled,
  withRealtorComToggled,
  withZillowToggled,
  withZillowVariant,
  withZumperToggled,
  zillowVariantForKind
} from "shared/components/SyndicationChannels/syndication";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  ALLOW_APPLICATIONS_LOCKED_BY_LISTED,
  ALLOW_APPLICATIONS_PMS_SYNCED,
  LISTED_PMS_SYNCED,
  LISTED_REQUIRES_APPLICATIONS
} from "shared/app/RentalInfoForm/constants";
import {
  isOverAffordableHousingComCap,
  AFFORDABLE_HOUSING_COM_OVER_CAP_TOOLTIP
} from "views/settings-integration/integrations/shared/affordableHousingComCap";

interface ListingSectionProps {
  propertyId: number;
  propertyKind: PropertyKind | null;
  unit: Unit;
}

const CLEARED_SYNDICATION: SyndicationState = {
  zillow: false,
  zillowSpotlight: false,
  zillowVariant: null,
  apartmentsCom: false,
  apartmentsComVariant: null,
  zumper: false,
  dwellsy: false,
  affordableHousingCom: false,
  rentalBeast: false,
  uloop: false,
  rentalSource: false,
  rentDuck: false,
  realtorCom: false
};

/** DOM id of the List unit switch row, the target of the header's "Go to List unit" link (CRM-1620). */
export const LIST_UNIT_SWITCH_ID = "list-unit-switch";

type TopLevelSource =
  | "listings"
  | "zillow"
  | "apartments"
  | "zumper"
  | "dwellsy"
  | "affordableHousingCom"
  | "rental_beast"
  | "uloop"
  | "rental_source"
  | "rent_duck"
  | "realtor_com";

const computeLastTopLevel = ({
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled,
  affordableHousingComEnabled,
  rentalBeastEnabled,
  uloopEnabled,
  rentalSourceEnabled,
  rentDuckEnabled,
  realtorComEnabled
}: {
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
  affordableHousingComEnabled: boolean;
  rentalBeastEnabled: boolean;
  uloopEnabled: boolean;
  rentalSourceEnabled: boolean;
  rentDuckEnabled: boolean;
  realtorComEnabled: boolean;
}): TopLevelSource => {
  if (realtorComEnabled) return "realtor_com";
  if (rentDuckEnabled) return "rent_duck";
  if (rentalSourceEnabled) return "rental_source";
  if (uloopEnabled) return "uloop";
  if (rentalBeastEnabled) return "rental_beast";
  if (affordableHousingComEnabled) return "affordableHousingCom";
  if (dwellsyEnabled) return "dwellsy";
  if (zumperEnabled) return "zumper";
  if (apartmentsComEnabled) return "apartments";
  if (zillowEnabled) return "zillow";
  return "listings";
};

const booleanLabel = (value?: boolean) => {
  if (value == null) return undefined;
  return value ? "On" : "Off";
};

const RowConnector = () => (
  <span aria-hidden className="flex items-center shrink-0">
    <span className="w-8 border-t border-content-100" />
    <span className="size-1.5 rounded-full bg-content-100" />
  </span>
);

const FeedVariantRows = ({
  selected,
  checked,
  disabled,
  onPick
}: {
  selected: ApartmentsComVariant | ZillowVariant | null;
  checked: boolean;
  disabled: boolean;
  onPick: (variant: ApartmentsComVariant | ZillowVariant) => void;
}) => (
  <div className="mt-2 ml-3 relative pl-5">
    <div className="space-y-2">
      <TreeRow>
        <SourceCheckbox
          label="Single Family"
          checked={checked && selected === "single_family"}
          disabled={disabled}
          onChange={() => onPick("single_family")}
        />
      </TreeRow>
      <TreeRow isLast>
        <SourceCheckbox
          label="Multi Family"
          checked={checked && selected === "multi_family"}
          disabled={disabled}
          onChange={() => onPick("multi_family")}
        />
      </TreeRow>
    </div>
  </div>
);

const syndicationEqual = (a: SyndicationState, b: SyndicationState) =>
  a.zillow === b.zillow &&
  a.zillowSpotlight === b.zillowSpotlight &&
  a.zillowVariant === b.zillowVariant &&
  a.apartmentsCom === b.apartmentsCom &&
  a.apartmentsComVariant === b.apartmentsComVariant &&
  a.zumper === b.zumper &&
  a.dwellsy === b.dwellsy &&
  a.affordableHousingCom === b.affordableHousingCom &&
  a.rentalBeast === b.rentalBeast &&
  a.uloop === b.uloop &&
  a.rentalSource === b.rentalSource &&
  a.rentDuck === b.rentDuck &&
  a.realtorCom === b.realtorCom;

const REPLICA_RETRY_DELAY_MS = 200;
const REPLICA_MAX_RETRIES = 3;
const AUTOSAVE_DEBOUNCE_MS = 500;

export const ListingSection = ({
  propertyId,
  propertyKind,
  unit
}: ListingSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const { isEverySyncEnabled } = useSettingsController();
  const { partner, type: partnerType } = useAuthContext();

  const defaultApartmentsComVariant = apartmentsVariantForKind(propertyKind);
  const defaultZillowVariant = zillowVariantForKind(propertyKind);

  const [linkIsActive, setLinkIsActive] = useState<boolean>(
    Boolean(unit.magic_link?.active)
  );
  const [unitListed, setUnitListed] = useState<boolean>(Boolean(unit.listed));
  const [syndication, setSyndication] = useState<SyndicationState>(() =>
    readSyndicationState(
      unit,
      defaultApartmentsComVariant,
      defaultZillowVariant
    )
  );

  const pmsManaged = isEverySyncEnabled && Boolean(unit.pms_id);
  const [overrideEdits, setOverrideEdits] = useState<{
    listed?: boolean;
    available_for_application?: boolean;
  }>({});
  const listedSynced =
    overrideEdits.listed ?? unit.pms_overrides?.listed ?? true;
  const allowApplyingSynced =
    overrideEdits.available_for_application ??
    unit.pms_overrides?.available_for_application ??
    true;

  const committedRef = useRef({
    linkIsActive,
    unitListed,
    syndication
  });
  const awaitingRefetchRef = useRef(false);
  const replicaRetryCountRef = useRef(0);
  const replicaRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const invalidatePropertyDetail = useCallback(() => {
    propertyKind === PropertyKind.SingleFamily
      ? queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        })
      : queryClient.invalidateQueries({
          queryKey: ["unit", String(propertyId), String(unit.id)]
        });
  }, [propertyKind, propertyId, unit.id, queryClient]);

  const handleAwaitingRefetch = useCallback(
    (matched: boolean): "done" | "continue" => {
      if (matched) {
        awaitingRefetchRef.current = false;
        replicaRetryCountRef.current = 0;
        if (replicaRetryTimerRef.current) {
          clearTimeout(replicaRetryTimerRef.current);
          replicaRetryTimerRef.current = null;
        }
        return "done";
      }

      if (replicaRetryCountRef.current < REPLICA_MAX_RETRIES) {
        replicaRetryCountRef.current += 1;
        if (replicaRetryTimerRef.current)
          clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = setTimeout(
          invalidatePropertyDetail,
          REPLICA_RETRY_DELAY_MS * replicaRetryCountRef.current
        );
        return "done";
      }

      awaitingRefetchRef.current = false;
      replicaRetryCountRef.current = 0;
      return "continue";
    },
    [invalidatePropertyDetail]
  );

  useEffect(
    () => () => {
      if (replicaRetryTimerRef.current)
        clearTimeout(replicaRetryTimerRef.current);
    },
    []
  );

  const enableAllowApplying = () => {
    setLinkIsActive(true);
  };

  const disableAllowApplying = () => {
    if (unitListed) {
      setLinkIsActive(false);
      setUnitListed(false);
      setSyndication(CLEARED_SYNDICATION);
    } else setLinkIsActive(false);
  };

  const enableShowOnPortfolio = () => {
    if (!linkIsActive) {
      setUnitListed(true);
      setLinkIsActive(true);
    } else setUnitListed(true);
  };

  const allowApplyingToggleLocked = linkIsActive && unitListed && listedSynced;
  const listedToggleLocked =
    !unitListed && !linkIsActive && allowApplyingSynced;

  let allowApplyingTooltip: string | null = null;
  if (allowApplyingSynced) allowApplyingTooltip = ALLOW_APPLICATIONS_PMS_SYNCED;
  else if (allowApplyingToggleLocked)
    allowApplyingTooltip = ALLOW_APPLICATIONS_LOCKED_BY_LISTED;

  let listedTooltip: string | null = null;
  if (listedSynced) listedTooltip = LISTED_PMS_SYNCED;
  else if (listedToggleLocked) listedTooltip = LISTED_REQUIRES_APPLICATIONS;

  const disableShowOnPortfolio = () => {
    setUnitListed(false);
    setSyndication(CLEARED_SYNDICATION);
  };

  const { mutate: updateUnitSettings, isPending } = useMutation({
    mutationKey: ["MAGIC_LINK_UPDATE", unit.magic_link?.id],
    mutationFn: async () => {
      const snapshot = {
        linkIsActive,
        unitListed,
        syndication
      };

      if (!unit.magic_link?.id) {
        committedRef.current = snapshot;
        return;
      }

      const payload = diffMagicLinkPayload(committedRef.current, snapshot);
      if (Object.keys(payload).length === 0) {
        committedRef.current = snapshot;
        return;
      }

      await addressApi.updateMagicLink(unit.magic_link.id, payload);

      committedRef.current = snapshot;
    },
    onSuccess: () => {
      awaitingRefetchRef.current = true;
      replicaRetryCountRef.current = 0;
      if (replicaRetryTimerRef.current) {
        clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = null;
      }
      invalidatePropertyDetail();
      invalidateNeedsLockboxCounts(queryClient);
    },
    onError: (error) => {
      const last = committedRef.current;
      setLinkIsActive(last.linkIsActive);
      setUnitListed(last.unitListed);
      setSyndication(last.syndication);
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  const pmsAvailability = unit.pms_values?.available_for_application;
  const pmsAllowApplyingValue =
    pmsAvailability ??
    (allowApplyingSynced ? Boolean(unit.magic_link?.active) : undefined);
  const pmsListedValue =
    pmsAvailability ?? (listedSynced ? Boolean(unit.listed) : undefined);

  const { mutate: updatePmsOverrides } = useMutation({
    mutationKey: ["UNIT_PMS_OVERRIDES_UPDATE", unit.id],
    mutationFn: async (overrides: {
      listed: boolean;
      available_for_application: boolean;
    }) => {
      await addressApi.updateUnitListingOverrides(unit.id, overrides);
      return overrides;
    },
    onSuccess: (overrides) => {
      if (pmsAvailability != null) {
        if (overrides.available_for_application) {
          setLinkIsActive(pmsAvailability);
          if (!pmsAvailability) {
            setUnitListed(false);
            setSyndication(CLEARED_SYNDICATION);
          }
        }
        if (overrides.listed) {
          setUnitListed(pmsAvailability);
          if (pmsAvailability) setLinkIsActive(true);
        }
      }
      invalidatePropertyDetail();
      invalidateNeedsLockboxCounts(queryClient);
    },
    onError: (error) => {
      setOverrideEdits({});
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  const handleAllowApplyingSyncChange = (synced: boolean) =>
    setOverrideEdits((prev) => ({
      ...prev,
      available_for_application: synced
    }));

  const handleListedSyncChange = (synced: boolean) =>
    setOverrideEdits((prev) => ({ ...prev, listed: synced }));

  useEffect(() => {
    const serverListed = unit.pms_overrides?.listed ?? true;
    const serverAllowApplying =
      unit.pms_overrides?.available_for_application ?? true;
    if (
      listedSynced === serverListed &&
      allowApplyingSynced === serverAllowApplying
    )
      return;

    const timeoutId = setTimeout(
      () =>
        updatePmsOverrides({
          listed: listedSynced,
          available_for_application: allowApplyingSynced
        }),
      AUTOSAVE_DEBOUNCE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [listedSynced, allowApplyingSynced]);

  useEffect(() => {
    if (isPending) return;

    const dirty =
      linkIsActive !== committedRef.current.linkIsActive ||
      unitListed !== committedRef.current.unitListed ||
      !syndicationEqual(syndication, committedRef.current.syndication);

    if (!dirty) return;

    const timeoutId = setTimeout(updateUnitSettings, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [linkIsActive, unitListed, syndication, isPending]);

  useEffect(() => {
    if (isPending) return;

    const serverActive = Boolean(unit.magic_link?.active);
    const serverListed = Boolean(unit.listed);
    const serverSyndication = readSyndicationState(
      unit,
      defaultApartmentsComVariant,
      defaultZillowVariant
    );

    const serverMatchesCommitted =
      serverActive === committedRef.current.linkIsActive &&
      serverListed === committedRef.current.unitListed &&
      syndicationEqual(serverSyndication, committedRef.current.syndication);

    if (
      awaitingRefetchRef.current &&
      handleAwaitingRefetch(serverMatchesCommitted) === "done"
    )
      return;

    if (serverMatchesCommitted) return;

    const localMatchesCommitted =
      linkIsActive === committedRef.current.linkIsActive &&
      unitListed === committedRef.current.unitListed &&
      syndicationEqual(syndication, committedRef.current.syndication);

    if (!localMatchesCommitted) return;

    setLinkIsActive(serverActive);
    setUnitListed(serverListed);
    setSyndication(serverSyndication);
    committedRef.current = {
      linkIsActive: serverActive,
      unitListed: serverListed,
      syndication: serverSyndication
    };
  }, [
    unit.magic_link?.active,
    unit.listed,
    unit.feed_zillow_enabled,
    unit.feed_zillow_spotlight_enabled,
    unit.feed_zillow_variant,
    unit.feed_apartments_com_enabled,
    unit.feed_apartments_com_variant,
    unit.feed_zumper_enabled,
    unit.feed_dwellsy_enabled,
    unit.feed_affordable_housing_com_enabled,
    unit.feed_rental_beast_enabled,
    unit.feed_uloop_enabled,
    unit.feed_rental_source_enabled,
    unit.feed_rent_duck_enabled,
    unit.feed_realtor_com_enabled,
    isPending,
    handleAwaitingRefetch,
    defaultApartmentsComVariant,
    defaultZillowVariant
  ]);

  const subjectLabel =
    propertyKind === PropertyKind.SingleFamily
      ? dictionary.property
      : dictionary.unit;
  const listToggleLabel = `List ${subjectLabel} on`;
  const allowApplyingLabel =
    propertyKind === null
      ? "Allow applying"
      : `Allow applying for ${subjectLabel}`;

  const overAffordableHousingComCap = isOverAffordableHousingComCap(
    unit.monthly_rent_amount?.cents
  );

  const toggleZillow = () =>
    setSyndication((prev) => withZillowToggled(prev, defaultZillowVariant));
  const pickZillowVariant = (variant: ZillowVariant) =>
    setSyndication((prev) => withZillowVariant(prev, variant));
  const toggleZumper = () => setSyndication(withZumperToggled);
  const toggleDwellsy = () => setSyndication(withDwellsyToggled);

  const toggleAffordableHousingCom = () =>
    setSyndication(withAffordableHousingComToggled);

  const toggleRentalBeast = () => setSyndication(withRentalBeastToggled);
  const toggleUloop = () => setSyndication(withUloopToggled);
  const toggleRentalSource = () => setSyndication(withRentalSourceToggled);
  const toggleRentDuck = () => setSyndication(withRentDuckToggled);
  const toggleRealtorCom = () => setSyndication(withRealtorComToggled);

  const toggleApartmentsCom = () =>
    setSyndication((prev) =>
      withApartmentsComToggled(prev, defaultApartmentsComVariant)
    );
  const pickApartmentsVariant = (variant: ApartmentsComVariant) =>
    setSyndication((prev) => withApartmentsVariant(prev, variant));

  return (
    <Card>
      <h3 className="text-base font-bold py-6 px-8 border-b border-alpha-40">
        Listing
      </h3>
      <div className="space-y-6 py-6 px-8">
        {pmsManaged ? (
          <div className="relative">
            <div className="flex items-center gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-fit shrink-0">
                    <Switch
                      label={allowApplyingLabel}
                      checked={linkIsActive}
                      disabled={
                        allowApplyingSynced || allowApplyingToggleLocked
                      }
                      className="text-base font-medium text-content-1000"
                      onChange={() => {
                        if (linkIsActive) disableAllowApplying();
                        else enableAllowApplying();
                      }}
                    />
                  </div>
                </TooltipTrigger>
                <Show if={Boolean(allowApplyingTooltip)}>
                  <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                    <p>{allowApplyingTooltip}</p>
                  </TooltipContent>
                </Show>
              </Tooltip>
              <RowConnector />
              <PmsSyncOption
                value={allowApplyingSynced}
                onChange={handleAllowApplyingSyncChange}
                name="available_for_application"
                order="manual-first"
                className="!mt-0"
              />
              <div className="ml-auto shrink-0">
                <PMSValue value={booleanLabel(pmsAllowApplyingValue)} />
              </div>
            </div>

            <span
              aria-hidden
              className="absolute border-l border-content-100 left-[22px] top-6 h-7"
            />
            <span
              aria-hidden
              className="absolute border-t border-content-100 left-[22px] top-[52px] w-[34px]"
            />

            <div className="ml-14 pt-4">
              {/* CRM-1620: same scroll/flash target as the manual branch below. */}
              <div
                id={LIST_UNIT_SWITCH_ID}
                className="-mx-2 -my-1 flex items-center gap-4 rounded-lg px-2 py-1 data-[flash]:animate-[list-unit-flash_1.5s_ease-out]"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-fit shrink-0">
                      <Switch
                        label={listToggleLabel}
                        checked={unitListed}
                        disabled={listedSynced || listedToggleLocked}
                        className="text-base font-medium text-content-1000"
                        onChange={() => {
                          if (unitListed) disableShowOnPortfolio();
                          else enableShowOnPortfolio();
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <Show if={Boolean(listedTooltip)}>
                    <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                      <p>{listedTooltip}</p>
                    </TooltipContent>
                  </Show>
                </Tooltip>
                <RowConnector />
                <PmsSyncOption
                  value={listedSynced}
                  onChange={handleListedSyncChange}
                  name="listed"
                  order="manual-first"
                  className="!mt-0"
                />
                <div className="ml-auto shrink-0">
                  <PMSValue value={booleanLabel(pmsListedValue)} />
                </div>
              </div>
              <SyndicationSourcesPanel
                unitListed={unitListed}
                hasListing={Boolean(unit.has_listing)}
                syndication={syndication}
                zillowEnabled={
                  Boolean(partner?.zillow_connected) &&
                  partnerType.isSyndication
                }
                apartmentsComEnabled={Boolean(
                  partner?.apartments_com_connected
                )}
                zumperEnabled={Boolean(partner?.zumper_connected)}
                dwellsyEnabled={Boolean(partner?.dwellsy_connected)}
                affordableHousingComEnabled={Boolean(
                  partner?.affordable_housing_com_connected
                )}
                overAffordableHousingComCap={overAffordableHousingComCap}
                rentalBeastEnabled={Boolean(partner?.rental_beast_connected)}
                uloopEnabled={Boolean(partner?.uloop_connected)}
                rentalSourceEnabled={Boolean(partner?.rental_source_connected)}
                rentDuckEnabled={Boolean(partner?.rent_duck_connected)}
                realtorComEnabled={Boolean(partner?.realtor_com_connected)}
                onToggleZillow={toggleZillow}
                onPickZillowVariant={pickZillowVariant}
                onToggleApartmentsCom={toggleApartmentsCom}
                onPickApartmentsVariant={pickApartmentsVariant}
                onToggleZumper={toggleZumper}
                onToggleDwellsy={toggleDwellsy}
                onToggleAffordableHousingCom={toggleAffordableHousingCom}
                onToggleRentalBeast={toggleRentalBeast}
                onToggleUloop={toggleUloop}
                onToggleRentalSource={toggleRentalSource}
                onToggleRentDuck={toggleRentDuck}
                onToggleRealtorCom={toggleRealtorCom}
              />
            </div>
          </div>
        ) : (
          <div className="relative">
            <Switch
              label={allowApplyingLabel}
              checked={linkIsActive}
              className="text-base font-medium text-content-1000"
              onChange={() => {
                if (linkIsActive) disableAllowApplying();
                else enableAllowApplying();
              }}
            />

            <span
              aria-hidden
              className="absolute border-l border-content-100 left-[22px] top-6 h-7"
            />
            <span
              aria-hidden
              className="absolute border-t border-content-100 left-[22px] top-[52px] w-[34px]"
            />

            <div className="ml-14 pt-4">
              {/* CRM-1620: the header's "Go to List unit" link scrolls here and flashes the row. */}
              <div
                id={LIST_UNIT_SWITCH_ID}
                className="-mx-2 -my-1 rounded-lg px-2 py-1 data-[flash]:animate-[list-unit-flash_1.5s_ease-out]"
              >
                <Switch
                  label={listToggleLabel}
                  checked={unitListed}
                  className="text-base font-medium text-content-1000"
                  onChange={() => {
                    if (unitListed) disableShowOnPortfolio();
                    else enableShowOnPortfolio();
                  }}
                />
              </div>
              <SyndicationSourcesPanel
                unitListed={unitListed}
                hasListing={Boolean(unit.has_listing)}
                syndication={syndication}
                zillowEnabled={
                  Boolean(partner?.zillow_connected) &&
                  partnerType.isSyndication
                }
                apartmentsComEnabled={Boolean(
                  partner?.apartments_com_connected
                )}
                zumperEnabled={Boolean(partner?.zumper_connected)}
                dwellsyEnabled={Boolean(partner?.dwellsy_connected)}
                affordableHousingComEnabled={Boolean(
                  partner?.affordable_housing_com_connected
                )}
                overAffordableHousingComCap={overAffordableHousingComCap}
                rentalBeastEnabled={Boolean(partner?.rental_beast_connected)}
                uloopEnabled={Boolean(partner?.uloop_connected)}
                rentalSourceEnabled={Boolean(partner?.rental_source_connected)}
                rentDuckEnabled={Boolean(partner?.rent_duck_connected)}
                realtorComEnabled={Boolean(partner?.realtor_com_connected)}
                onToggleZillow={toggleZillow}
                onPickZillowVariant={pickZillowVariant}
                onToggleApartmentsCom={toggleApartmentsCom}
                onPickApartmentsVariant={pickApartmentsVariant}
                onToggleZumper={toggleZumper}
                onToggleDwellsy={toggleDwellsy}
                onToggleAffordableHousingCom={toggleAffordableHousingCom}
                onToggleRentalBeast={toggleRentalBeast}
                onToggleUloop={toggleUloop}
                onToggleRentalSource={toggleRentalSource}
                onToggleRentDuck={toggleRentDuck}
                onToggleRealtorCom={toggleRealtorCom}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

interface SyndicationSourcesPanelProps {
  unitListed: boolean;
  hasListing: boolean;
  syndication: SyndicationState;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
  affordableHousingComEnabled: boolean;
  overAffordableHousingComCap: boolean;
  rentalBeastEnabled: boolean;
  uloopEnabled: boolean;
  rentalSourceEnabled: boolean;
  rentDuckEnabled: boolean;
  realtorComEnabled: boolean;
  onToggleZillow: () => void;
  onPickZillowVariant: (variant: ZillowVariant) => void;
  onToggleApartmentsCom: () => void;
  onPickApartmentsVariant: (variant: ApartmentsComVariant) => void;
  onToggleZumper: () => void;
  onToggleDwellsy: () => void;
  onToggleAffordableHousingCom: () => void;
  onToggleRentalBeast: () => void;
  onToggleUloop: () => void;
  onToggleRentalSource: () => void;
  onToggleRentDuck: () => void;
  onToggleRealtorCom: () => void;
}

const SyndicationSourcesPanel = ({
  unitListed,
  hasListing,
  syndication,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled,
  affordableHousingComEnabled,
  overAffordableHousingComCap,
  rentalBeastEnabled,
  uloopEnabled,
  rentalSourceEnabled,
  rentDuckEnabled,
  realtorComEnabled,
  onToggleZillow,
  onPickZillowVariant,
  onToggleApartmentsCom,
  onPickApartmentsVariant,
  onToggleZumper,
  onToggleDwellsy,
  onToggleAffordableHousingCom,
  onToggleRentalBeast,
  onToggleUloop,
  onToggleRentalSource,
  onToggleRentDuck,
  onToggleRealtorCom
}: SyndicationSourcesPanelProps) => {
  const sectionDisabled = !unitListed || !hasListing;

  const lastTopLevel = computeLastTopLevel({
    zillowEnabled,
    apartmentsComEnabled,
    zumperEnabled,
    dwellsyEnabled,
    affordableHousingComEnabled,
    rentalBeastEnabled,
    uloopEnabled,
    rentalSourceEnabled,
    rentDuckEnabled,
    realtorComEnabled
  });

  return (
    <div className="mt-2 ml-6 relative pl-5">
      <div className="space-y-2">
        <TreeRow>
          <SourceCheckbox
            label="Portfolio page"
            icon={<BoomBrandMark />}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <TreeRow isLast={lastTopLevel === "listings"}>
          <SourceCheckbox
            label="Listings page"
            icon={<BoomBrandMark />}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <Show if={zillowEnabled}>
          <TreeRow isLast={lastTopLevel === "zillow"}>
            <SourceCheckbox
              label="Zillow"
              icon={<SourceLogo id={IntegrationId.ZILLOW} />}
              checked={unitListed && syndication.zillow}
              disabled={sectionDisabled}
              onChange={onToggleZillow}
            />
            <FeedVariantRows
              selected={syndication.zillowVariant}
              checked={unitListed && syndication.zillow}
              disabled={sectionDisabled || !syndication.zillow}
              onPick={onPickZillowVariant}
            />
          </TreeRow>
        </Show>
        <Show if={apartmentsComEnabled}>
          <TreeRow isLast={lastTopLevel === "apartments"}>
            <SourceCheckbox
              label="Apartments.com"
              icon={<SourceLogo id={IntegrationId.APARTMENTS_COM} />}
              checked={unitListed && syndication.apartmentsCom}
              disabled={sectionDisabled}
              onChange={onToggleApartmentsCom}
            />
            <FeedVariantRows
              selected={syndication.apartmentsComVariant}
              checked={unitListed && syndication.apartmentsCom}
              disabled={sectionDisabled || !syndication.apartmentsCom}
              onPick={onPickApartmentsVariant}
            />
          </TreeRow>
        </Show>
        <Show if={zumperEnabled}>
          <TreeRow isLast={lastTopLevel === "zumper"}>
            <SourceCheckbox
              label="Zumper"
              icon={<SourceLogo id={IntegrationId.ZUMPER} />}
              checked={unitListed && syndication.zumper}
              disabled={sectionDisabled}
              onChange={onToggleZumper}
            />
          </TreeRow>
        </Show>
        <Show if={dwellsyEnabled}>
          <TreeRow isLast={lastTopLevel === "dwellsy"}>
            <SourceCheckbox
              label="Dwellsy"
              icon={<SourceLogo id={IntegrationId.DWELLSY} />}
              checked={unitListed && syndication.dwellsy}
              disabled={sectionDisabled}
              onChange={onToggleDwellsy}
            />
          </TreeRow>
        </Show>
        <Show if={affordableHousingComEnabled}>
          <TreeRow isLast={lastTopLevel === "affordableHousingCom"}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-fit">
                  <SourceCheckbox
                    label="AffordableHousing.com"
                    icon={
                      <SourceLogo id={IntegrationId.AFFORDABLE_HOUSING_COM} />
                    }
                    checked={
                      unitListed &&
                      syndication.affordableHousingCom &&
                      !overAffordableHousingComCap
                    }
                    disabled={sectionDisabled || overAffordableHousingComCap}
                    onChange={onToggleAffordableHousingCom}
                  />
                </span>
              </TooltipTrigger>
              <Show if={overAffordableHousingComCap}>
                <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                  <p>{AFFORDABLE_HOUSING_COM_OVER_CAP_TOOLTIP}</p>
                </TooltipContent>
              </Show>
            </Tooltip>
          </TreeRow>
        </Show>
        <Show if={rentalBeastEnabled}>
          <TreeRow isLast={lastTopLevel === "rental_beast"}>
            <SourceCheckbox
              label="Rental Beast"
              icon={<SourceLogo id={IntegrationId.RENTAL_BEAST} />}
              checked={unitListed && syndication.rentalBeast}
              disabled={sectionDisabled}
              onChange={onToggleRentalBeast}
            />
          </TreeRow>
        </Show>
        <Show if={uloopEnabled}>
          <TreeRow isLast={lastTopLevel === "uloop"}>
            <SourceCheckbox
              label="Uloop"
              icon={<SourceLogo id={IntegrationId.ULOOP} />}
              checked={unitListed && syndication.uloop}
              disabled={sectionDisabled}
              onChange={onToggleUloop}
            />
          </TreeRow>
        </Show>
        <Show if={rentalSourceEnabled}>
          <TreeRow isLast={lastTopLevel === "rental_source"}>
            <SourceCheckbox
              label="Rental Source"
              icon={<SourceLogo id={IntegrationId.RENTAL_SOURCE} />}
              checked={unitListed && syndication.rentalSource}
              disabled={sectionDisabled}
              onChange={onToggleRentalSource}
            />
          </TreeRow>
        </Show>
        <Show if={rentDuckEnabled}>
          <TreeRow isLast={lastTopLevel === "rent_duck"}>
            <SourceCheckbox
              label="Rent Duck"
              icon={<SourceLogo id={IntegrationId.RENT_DUCK} />}
              checked={unitListed && syndication.rentDuck}
              disabled={sectionDisabled}
              onChange={onToggleRentDuck}
            />
          </TreeRow>
        </Show>
        <Show if={realtorComEnabled}>
          <TreeRow isLast={lastTopLevel === "realtor_com"}>
            <SourceCheckbox
              label="Realtor.com"
              icon={<SourceLogo id={IntegrationId.REALTOR_COM} />}
              checked={unitListed && syndication.realtorCom}
              disabled={sectionDisabled}
              onChange={onToggleRealtorCom}
            />
          </TreeRow>
        </Show>
      </div>
    </div>
  );
};
